import { FormBlock, FormCard, ValidationList } from '@qbe/ads-blocks-forms';
import {
  Box,
  Button,
  Form,
  RadioSelectableCard,
  RadioSelectableCardProps,
  Spacer,
  Spinner,
  Typography,
} from '@qbe/ads-core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';

import { TAX_AUDIT_SI_OPTIONS } from '~/constants/domain-specific';
import { useAppContent, useAppDispatch, useCoverDetails, useCoverDetailsEffects } from '~/hooks';
import { useFormWithAnalytics } from '~/hooks/useFormWithAnalytics';
import { updateTaxAuditCover } from '~/slices/coverDetails';
import { addValidatedCover } from '~/slices/selectCover';
import { qalFormCompleteAction } from '~/thunks/analytics';
import { Cover } from '~/types/cover';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';
import { CurrencyFormat, currencyFormatter } from '~/utils/currencyFormatter';

interface TaxAuditCover {
  sumInsured: string | null;
}

const CoverDetailsTaxAudit = () => {
  const { getTextContent: getSharedContent } = useAppContent({
    basePath: 'shared',
  });

  const { getTextContent } = useAppContent({ basePath: 'dsme.coverDetailsTaxAudit' });
  const { taxAudit } = useCoverDetails();
  const { handleSubmit, control, formState } = useFormWithAnalytics<TaxAuditCover>({
    mode: 'onChange',
    defaultValues: {
      sumInsured: taxAudit?.sumInsured ? `${taxAudit.sumInsured}` : null,
    },
  });
  const dispatch = useAppDispatch();
  const [submitted, setSubmitted] = useState(false);

  const { quotePremiums, isLoading, buttonText } = useCoverDetailsEffects({
    cover: Cover.TaxAudit,
  });

  const handleFormComplete = useCallback(() => {
    if (isFormStartStarted()) dispatch(qalFormCompleteAction());
  }, [dispatch]);
  const onSubmit = handleSubmit((data: TaxAuditCover | undefined) => {
    if (!data || !data.sumInsured) {
      console.error('Form data is incomplete');
      return;
    }
    dispatch(updateTaxAuditCover({ sumInsured: Number(data.sumInsured) }));
    dispatch(addValidatedCover(Cover.TaxAudit));
    handleFormComplete();
    setSubmitted(true);
  });

  useEffect(() => {
    if (submitted) {
      // TODO: might need to move to handleSubmit - possible solution : Debounce
      quotePremiums();
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSubmitted(false);
    }
  }, [submitted, quotePremiums]);

  const radioList = useMemo<RadioSelectableCardProps['radioList']>(
    () =>
      TAX_AUDIT_SI_OPTIONS.map((value, key) => ({
        id: String(key),
        value: String(value),
        label: currencyFormatter(value, CurrencyFormat.Comma),
        labelProps: {
          variant: 'label2',
          weight: 'bold',
        },
      })),
    []
  );

  return (
    <FormCard variant="sectionTitle" title={getTextContent('title')}>
      <Typography variant="body1" component="h1" fontWeight="fontWeightMedium" gutterBottom>
        {getTextContent('fields.inclusions.title')}
      </Typography>

      <ValidationList
        list={[
          {
            id: '1',
            item: 'Protection for your business against the cost of statutory tax audits',
          },
          {
            id: '2',
            item: `Costs includes fees, charges, expenses, and payments made to accountants, assessors,
            consultants, investigators, or mediators appointed by an alternative dispute resolution
            center (arising from a tax audit from the ATO or defined audits from Commonwealth or State
            departments)`,
          },
          {
            id: '3',
            item: 'Cover for expenses incurred in obtaining documentary evidence for claim substantiation.',
          },
        ]}
        sx={{ '& > .ValidationList__list': { gridTemplateColumns: 'unset' } }}
      />
      <Spacer size="md" />

      <Form onSubmit={onSubmit} style={{ width: '100%', maxWidth: '400px', textAlign: 'center' }}>
        <Box id="money-si" data-testid="money-si">
          <Controller
            rules={{ required: true }}
            control={control}
            name="sumInsured"
            render={({ field }) => (
              <RadioSelectableCard
                {...field}
                value={field.value ?? null}
                defaultValue={taxAudit?.sumInsured}
                data-track="Select Your Sum Insured"
                data-track-value="capture"
                direction="vertical"
                title={getTextContent('fields.sumInsured.title')}
                radioList={radioList}
              />
            )}
          />
        </Box>

        <Spinner loading={isLoading} description={getSharedContent('loadingLabel')} />
        <FormBlock preset="button">
          <Button
            label={buttonText}
            type="submit"
            data-track={`${buttonText} - taxaudit`}
            disabled={!formState.isValid || isLoading}
          />
        </FormBlock>
      </Form>
    </FormCard>
  );
};
export default CoverDetailsTaxAudit;
